import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input, OnDestroy, OnInit,
  Output,
  QueryList, Renderer2,
  ViewChild,
  ViewChildren,
  ViewContainerRef
} from '@angular/core';
import {Router} from "@angular/router";
import {Subscription} from "rxjs";
import {filter} from "rxjs/operators";

import {isRegion2, REGION2} from "../../defines/gukeo.const";
import {SimpleDialect} from "../../models/dialect.model";
import {SimpleWord} from "../../models/simple-word.model";
import {
  GetRegion2Css,
  GetRegion2CssPos,
  GetRegion2Name,
} from "../../defines/desktop.const";
import {RESULT_LINKPOPUP} from "../../defines/client.const";
import {WordHistoryService} from "../../services/word/word-history.service";
import {ViewContainerRefDirective} from "../../directives/view-container-ref/view-container-ref.directive";
import {DialectHistoryService} from "../../services/dialect/dialect-history.service";
import {DynamicComponentService} from "../../services/dynamic-component/dynamic-component.service";
import {GlobalEventService} from "../../services/global-event/global-event.service";
import {DialectLinkMixinFunction} from "../../mixin/dialect-link.mixin";
import {ClickOutsideMixinFunction} from "../../mixin/click-outside.mixin";
import {
  BubbleLayoutBox,
  BubblePlacement,
  resolveBubblePlacements,
} from "./dialect-map-bubble-layout";


const dlMixin = DialectLinkMixinFunction(
  ClickOutsideMixinFunction(

    class Parent {
      constructor(
        public router: Router,
        public elementRef: ElementRef,
        public renderer: Renderer2,
        public wHistorySvc: WordHistoryService,
        public dHistorySvc: DialectHistoryService,
        public dynamicSvc: DynamicComponentService,
        public globalEventSvc: GlobalEventService,
      ) {}
    }
  )
)

class RegionBox {
  region2: REGION2;
  names: Array<string>;
  ids: Array<string>; // 지방어 아이디 목록

  constructor(region2: REGION2, name: string, id: string) {
    this.region2 = region2;
    this.names = [name];
    this.ids = [id];
  }

  public get area(): string {
    return GetRegion2Name(this.region2);
  }

  public add(name: string, id: string): void {
    this.names.push(name);
    this.ids.push(id);
  }

  public get names_txt(): string {
    return this.names.join(', ');
  }

  public get ids_txt(): string {
    return this.ids.join(',');
  }
}

type BubbleLayoutElement = BubbleLayoutBox & {
  el: HTMLElement;
};


@Component({
  selector: 'app-dialect-map-popup',
  styleUrls: ['./dialect-map-popup.component.scss'],
  template: `
    <article *ngIf="listRegionBox"
             class="place-layout1">

      <div class="place-wrap">
        <a class="place-close-btn" (click)="onClose($event)"></a>

        <div class="place-head-box">
          <div class="place-tit-box">다른 지역, 같은 말</div>
          <div *ngIf="word"
               class="place-info-box">

            <div class="subject-box">표준말</div>

            <div class="con-box"
                 [class.linkable]="word.id">
              <app-word-disp [word]="word"
                             [showLevel]="false"
                             [linkPopup]="true"
                             [popupZIndex]="501"
                             class="smaller no-link-arrow"></app-word-disp>
            </div>

          </div>
        </div>

        <div class="place-body-box">

          <div #placeViewBox
               class="place-view-box">

            <div class="place-map-box">
              <img src="assets/img/img_place_map.svg"
                   alt="이미지"
                   (load)="queueBubbleLayout()"
                   (error)="queueBubbleLayout()">
            </div>


            <div *ngFor="let dialect of listRegionBox"
                 #placeItemBox
                 class="place-item-box type-{{ GetRegion2Css(dialect.region2) }} region2-{{ GetRegion2CssPos(dialect.region2) }}"
                 (click)="onSelectRegion($event, dialect.ids_txt)">
              <ng-container viewContainerRef></ng-container>
              <div class="place-name-box">
                <article class="place-list-layout">
                  <div class="place-label-list">
                    <div class="place-label-box main-type{{ GetRegion2Css(dialect.region2) }}">{{ dialect.area }}</div>
                  </div>
                </article>
              </div>
              <div class="place-word-box">{{ dialect.names_txt }}</div>
            </div>

          </div>
        </div>

      </div>


    </article>

  `,
})
export class DialectMapPopupComponent extends dlMixin implements OnInit, AfterViewInit, OnDestroy {
  @ViewChildren(ViewContainerRefDirective) viewContainerRefs?: QueryList<ViewContainerRefDirective>;
  @ViewChild('placeViewBox') placeViewBox?: ElementRef<HTMLElement>;
  @ViewChildren('placeItemBox') placeItemBoxes?: QueryList<ElementRef<HTMLElement>>;
  @Output() close = new EventEmitter<string | null>();

  @Input() word: SimpleWord | null = null;
  @Input() set dialects( values: Array<SimpleDialect> ) {
    const mapRegionBox = new Map<REGION2, RegionBox>();
    values.forEach(dialect => {
      if (dialect.area1 && isRegion2(dialect.area1)) {
        const regionBox = mapRegionBox.get(dialect.area1);
        if (regionBox) {
          regionBox.add(dialect.word, dialect.id);
        }
        else {
          mapRegionBox.set(dialect.area1, new RegionBox(dialect.area1, dialect.word, dialect.id));
        }
      }
    });

    this.listRegionBox = Array.from(mapRegionBox.values());
  }

  protected readonly GetRegion2Css = GetRegion2Css;
  protected readonly GetRegion2CssPos = GetRegion2CssPos;


  protected listRegionBox?: Array<RegionBox>;
  private placeItemBoxes$?: Subscription;
  private layoutQueued = false;
  private layoutRafId?: number;
  private layoutRetryTimeoutId?: ReturnType<typeof setTimeout>;
  private layoutRetries = 0;
  private static readonly MAX_LAYOUT_RETRIES = 5;


  constructor(
    public override router: Router,
    public override elementRef: ElementRef,
    public override renderer: Renderer2,
    public override wHistorySvc: WordHistoryService,
    public override dHistorySvc: DialectHistoryService,
    public override dynamicSvc: DynamicComponentService,
    public override globalEventSvc: GlobalEventService,
    private container: ViewContainerRef,
  ) {
    super(router, elementRef, renderer, wHistorySvc, dHistorySvc, dynamicSvc, globalEventSvc);
  }

  ngOnInit() {
    console.warn('DialectMapPopupComponent::ngOnInit()');
    this.coInit(() => this.close.emit(null));
  }

  ngAfterViewInit() {
    this.placeItemBoxes$ = this.placeItemBoxes?.changes.subscribe(() => this.queueBubbleLayout());
    this.queueBubbleLayout();
  }

  ngOnDestroy() {
    if (this.layoutRafId !== undefined) {
      cancelAnimationFrame(this.layoutRafId);
      this.layoutRafId = undefined;
    }
    if (this.layoutRetryTimeoutId !== undefined) {
      clearTimeout(this.layoutRetryTimeoutId);
      this.layoutRetryTimeoutId = undefined;
    }
    this.placeItemBoxes$?.unsubscribe();
    this.coDestroy();
  }

  onClose(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.globalEventSvc.Click(event);

    this.close.emit(null)
  }


  onSelectRegion(event: MouseEvent, dialect_ids: string) {
    console.warn('DialectMapPopupComponent::onSelectRegion', dialect_ids, event.target as HTMLElement);
    event.preventDefault();
    event.stopPropagation();
    this.globalEventSvc.Click(event);

    const clickedElement = event.target as HTMLElement;
    const container = this.getViewContainerRef(clickedElement) ?? this.container;

    this.OpenDialectLink(dialect_ids, container, {
      placement: 'auto',
      scrollIntoView: false,
    }).pipe(
      filter(result => result === RESULT_LINKPOPUP.GOTO_LINK)
    ).subscribe(() => this.close.emit(dialect_ids));
  }

  private getViewContainerRef(el: HTMLElement): ViewContainerRef | undefined {
    const viewContainerRef = this.viewContainerRefs?.find(directive => {
      // console.log('DialectMapPopupComponent::getViewContainerRef find()', directive.elementRef.nativeElement, 'el=', el);

      // NOTE: viewContainerRef directive가 붙은 위치로 팝업을 연결한다.
      return directive.elementRef.nativeElement.parentNode.contains(el);
      // return isParentHTMLElement(el, directive.elementRef.nativeElement.parentNode);
    })

    console.log('DialectMapPopupComponent::getViewContainerRef return =', viewContainerRef?.elementRef.nativeElement, this.viewContainerRefs);

    return viewContainerRef?.viewContainerRef;
  }

  protected queueBubbleLayout(): void {
    if (this.layoutQueued) {
      return;
    }
    this.layoutQueued = true;

    // 더블 rAF: 진행 중일 수 있는 변화 감지·레이아웃이 완전히 반영된 뒤
    // getBoundingClientRect를 읽기 위해 한 프레임을 더 미룬다. 단일 rAF로는
    // 일부 환경에서 첫 프레임 시점에 viewbox 크기가 0으로 잡히는 사례가 있어
    // 한 프레임 더 미루는 안전장치를 둔다.
    this.layoutRafId = requestAnimationFrame(() => {
      this.layoutRafId = requestAnimationFrame(() => {
        this.layoutQueued = false;
        this.layoutRafId = undefined;
        this.layoutBubbles();
      });
    });
  }

  private layoutBubbles(): void {
    const viewBox = this.placeViewBox?.nativeElement;
    const itemBoxes = this.placeItemBoxes?.toArray() ?? [];

    if (!viewBox || itemBoxes.length === 0) {
      return;
    }

    itemBoxes.forEach(item => this.resetBubblePosition(item.nativeElement));

    const viewRect = viewBox.getBoundingClientRect();
    if (viewRect.width <= 0 || viewRect.height <= 0) {
      // 팝업 직후 모달 애니메이션 등으로 .place-view-box가 0 크기일 수 있다.
      // 더블 rAF만으로 부족한 경우의 안전장치로 짧게 재시도하되,
      // MAX_LAYOUT_RETRIES로 무한 루프를 방지한다.
      if (this.layoutRetries < DialectMapPopupComponent.MAX_LAYOUT_RETRIES) {
        this.layoutRetries++;
        this.layoutRetryTimeoutId = setTimeout(() => {
          this.layoutRetryTimeoutId = undefined;
          this.queueBubbleLayout();
        }, 50);
      }
      else {
        // 한도 도달: 말풍선이 SCSS 기본 위치(겹침 가능)로 보이는 부작용을 알린다.
        // 카운터를 리셋해 이후 (load)/(error)/QueryList changes로 재트리거되면 다시 시도할 수 있게 한다.
        console.warn(
          `DialectMapPopupComponent: place-view-box가 ${DialectMapPopupComponent.MAX_LAYOUT_RETRIES}회 재시도 후에도 0 크기 — SCSS 기본 위치로 표시`,
          {width: viewRect.width, height: viewRect.height},
        );
        this.layoutRetries = 0;
      }
      return;
    }
    this.layoutRetries = 0;

    const boxes = itemBoxes
      .map((item, index) => this.createLayoutBox(item.nativeElement, index, viewRect))
      .filter((box): box is BubbleLayoutElement => box !== null);

    const placements = resolveBubblePlacements(boxes, {
      width: viewRect.width,
      height: viewRect.height,
    });
    placements.forEach(placement => {
      const box = boxes.find(item => item.index === placement.index);
      if (box) {
        this.applyBubblePosition(box.el, placement);
      }
    });
  }

  private resetBubblePosition(el: HTMLElement): void {
    el.style.left = '';
    el.style.top = '';
    el.style.right = '';
    el.style.bottom = '';
  }

  private createLayoutBox(el: HTMLElement, index: number, viewRect: DOMRect): BubbleLayoutElement | null {
    const rect = el.getBoundingClientRect();
    const width = Math.ceil(rect.width);
    const height = Math.ceil(rect.height);

    if (width <= 0 || height <= 0) {
      return null;
    }

    const baseX = rect.left - viewRect.left;
    const baseY = rect.top - viewRect.top;

    return {
      index,
      el,
      width,
      height,
      baseX,
      baseY,
    };
  }

  private applyBubblePosition(el: HTMLElement, placement: BubblePlacement): void {
    el.style.left = `${placement.x}px`;
    el.style.top = `${placement.y}px`;
    el.style.right = 'auto';
    el.style.bottom = 'auto';
  }
}
